import PageSEO from '../components/PageSEO';
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Brain, AlertTriangle, Heart, Moon, Activity, Zap, Shield, Focus,
  ChevronRight, ChevronLeft, Phone, Check, X, Upload, FileText,
  User, ClipboardCheck
} from 'lucide-react';
import Header from '../components/Header';
import {
  MODULES, COMMON_QUESTIONS, MODULE_QUESTIONS,
  FREQ_4, calcTotalQuestions, buildModuleQuestions, DOMAIN_QUESTION_MAP,
} from '../data/onboardingConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = { Brain, Focus, AlertTriangle, Heart, Moon, Activity, Zap, Shield };

/* ══════════════════════════════════════════════════════════════
   STEP PHASES
   ══════════════════════════════════════════════════════════════ */
const PHASE = {
  AREA_SELECT: 'area_select',
  COMMON: 'common',
  MODULES: 'modules',
  DOC_UPLOAD: 'doc_upload',
};

/* ══════════════════════════════════════════════════════════════
   REUSABLE QUESTION RENDERERS
   ══════════════════════════════════════════════════════════════ */
function QuestionNumber({ num, total }) {
  return (
    <div className="flex items-center gap-2 mb-1">
      <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: 'rgba(34,211,238,0.1)', color: '#67E8F9' }}>
        {num} / {total}
      </span>
    </div>
  );
}

function YesNoInput({ value, onChange, id }) {
  return (
    <div className="flex gap-3" data-testid={`yesno-${id}`}>
      {[{ v: true, l: 'Evet' }, { v: false, l: 'Hayır' }].map(o => (
        <button key={String(o.v)} onClick={() => onChange(o.v)} type="button"
          className="flex-1 py-3 rounded-xl text-sm font-semibold transition-all"
          style={{
            background: value === o.v ? (o.v ? 'rgba(251,191,36,0.12)' : 'rgba(34,211,238,0.10)') : 'rgba(255,255,255,0.03)',
            border: `1px solid ${value === o.v ? (o.v ? 'rgba(251,191,36,0.35)' : 'rgba(34,211,238,0.30)') : 'rgba(255,255,255,0.08)'}`,
            color: value === o.v ? (o.v ? '#FCD34D' : '#67E8F9') : '#94A3B8',
          }}
          data-testid={`yesno-${id}-${o.v}`}>
          {o.l}
        </button>
      ))}
    </div>
  );
}

function SingleSelect({ options, value, onChange, id }) {
  const isLongList = options.length > 20;
  return (
    <div className={isLongList ? '' : 'space-y-2'} data-testid={`single-${id}`}
      style={isLongList ? { maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4, paddingRight: 4 } : undefined}>
      {options.map(o => (
        <button key={o.value} onClick={() => onChange(o.value)} type="button"
          className={`w-full text-left ${isLongList ? 'px-4 py-2.5' : 'px-4 py-3'} rounded-xl text-sm transition-all`}
          style={{
            background: value === o.value ? 'rgba(34,211,238,0.1)' : 'rgba(255,255,255,0.02)',
            border: `1px solid ${value === o.value ? 'rgba(34,211,238,0.35)' : 'rgba(255,255,255,0.06)'}`,
            color: value === o.value ? '#67E8F9' : '#CBD5E1',
            flexShrink: 0,
          }}
          data-testid={`opt-${id}-${o.value}`}>
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0" style={{
              borderColor: value === o.value ? '#22D3EE' : 'rgba(255,255,255,0.15)',
            }}>
              {value === o.value && <div className="w-2 h-2 rounded-full" style={{ background: '#22D3EE' }} />}
            </div>
            {o.label}
          </div>
        </button>
      ))}
    </div>
  );
}

const FREQ_COLORS = {
  hayir: { bg: 'rgba(34,211,238,0.10)', border: 'rgba(34,211,238,0.30)', text: '#67E8F9' },
  nadiren: { bg: 'rgba(52,211,153,0.10)', border: 'rgba(52,211,153,0.30)', text: '#6EE7B7' },
  bazen: { bg: 'rgba(251,191,36,0.10)', border: 'rgba(251,191,36,0.30)', text: '#FCD34D' },
  sik: { bg: 'rgba(251,146,60,0.10)', border: 'rgba(251,146,60,0.30)', text: '#FDBA74' },
};

function Freq4Input({ value, onChange, id }) {
  return (
    <div className="grid grid-cols-2 gap-2" data-testid={`freq4-${id}`}>
      {FREQ_4.map(o => {
        const c = FREQ_COLORS[o.value] || FREQ_COLORS.hayir;
        return (
          <button key={o.value} onClick={() => onChange(o.value)} type="button"
            className="py-3 rounded-xl text-sm font-medium transition-all"
            style={{
              background: value === o.value ? c.bg : 'rgba(255,255,255,0.02)',
              border: `1px solid ${value === o.value ? c.border : 'rgba(255,255,255,0.06)'}`,
              color: value === o.value ? c.text : '#94A3B8',
            }}
            data-testid={`freq4-${id}-${o.value}`}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, multiline, id }) {
  const C = multiline ? 'textarea' : 'input';
  return (
    <C value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder || ''}
      className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all"
      style={{
        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
        color: '#E2E8F0', minHeight: multiline ? 80 : undefined, resize: multiline ? 'vertical' : undefined,
      }}
      data-testid={`text-${id}`} />
  );
}

function NumberInput({ value, onChange, placeholder, min, max, id }) {
  return (
    <input type="number" value={value || ''} onChange={e => onChange(e.target.value)}
      placeholder={placeholder || ''} min={min} max={max}
      className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all"
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', color: '#E2E8F0' }}
      data-testid={`number-${id}`} />
  );
}

/* Render followup questions (flat, max 2 levels) */
function RenderFollowups({ followups, answers, setAnswer, parentAnswer }) {
  if (!followups || !parentAnswer) return null;
  return (
    <>
      {followups.filter(fu => fu.showWhen === true ? parentAnswer === true : parentAnswer === fu.showWhen).map(fu => (
        <div key={fu.id} className="ml-4 mt-3 pl-4" style={{ borderLeft: '2px solid rgba(34,211,238,0.15)' }}>
          <p className="text-sm text-white/60 mb-2">{fu.text}</p>
          {fu.type === 'yesno' && (
            <YesNoInput id={fu.id} value={answers[fu.id]} onChange={v => setAnswer(fu.id, v)} />
          )}
          {fu.type === 'single' && <SingleSelect id={fu.id} options={fu.options} value={answers[fu.id]} onChange={v => setAnswer(fu.id, v)} />}
          {fu.type === 'text' && <TextInput id={fu.id} value={answers[fu.id]} onChange={v => setAnswer(fu.id, v)} placeholder={fu.placeholder} />}
          {fu.type === 'textarea' && <TextInput id={fu.id} value={answers[fu.id]} onChange={v => setAnswer(fu.id, v)} multiline />}
          {/* Level 2 followups (non-recursive) */}
          {fu.type === 'yesno' && fu.followups && answers[fu.id] === true && fu.followups.map(fu2 => (
            <div key={fu2.id} className="ml-4 mt-3 pl-4" style={{ borderLeft: '2px solid rgba(34,211,238,0.1)' }}>
              <p className="text-sm text-white/50 mb-2">{fu2.text}</p>
              {fu2.type === 'yesno' && <YesNoInput id={fu2.id} value={answers[fu2.id]} onChange={v => setAnswer(fu2.id, v)} />}
              {fu2.type === 'single' && <SingleSelect id={fu2.id} options={fu2.options} value={answers[fu2.id]} onChange={v => setAnswer(fu2.id, v)} />}
              {fu2.type === 'text' && <TextInput id={fu2.id} value={answers[fu2.id]} onChange={v => setAnswer(fu2.id, v)} placeholder={fu2.placeholder} />}
              {fu2.type === 'textarea' && <TextInput id={fu2.id} value={answers[fu2.id]} onChange={v => setAnswer(fu2.id, v)} multiline />}
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

/* ══════════════════════════════════════════════════════════════
   MAIN ONBOARDING PAGE
   ══════════════════════════════════════════════════════════════ */
export default function OnboardingPage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [phase, setPhase] = useState(PHASE.AREA_SELECT);
  const [selectedModules, setSelectedModules] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentQuestionIdx, setCurrentQuestionIdx] = useState(0);
  const [currentModuleGroupIdx, setCurrentModuleGroupIdx] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.onboarding_completed) navigate('/dashboard');
  }, [user, navigate]);

  const setAnswer = useCallback((id, value) => {
    setAnswers(prev => ({ ...prev, [id]: value }));
  }, []);

  /* Auto-advance: seçim yapıldığında otomatik sonraki soruya geç */
  const setAnswerAndAdvance = useCallback((id, value) => {
    setAnswers(prev => ({ ...prev, [id]: value }));
    // Küçük gecikme ile sonraki soruya geç (kullanıcı seçimini görsün)
    setTimeout(() => {
      goNextRef.current();
    }, 350);
  }, []);

  const goNextRef = React.useRef(() => {});

  // ── Computed data ──
  const moduleGroups = buildModuleQuestions(selectedModules);
  const totalEstimated = selectedModules.length > 0 ? calcTotalQuestions(selectedModules) : 0;

  // ── Current question list based on phase ──
  let currentQuestions = [];
  let phaseTitle = '';
  let phaseSubtitle = '';
  let phaseColor = '#22D3EE';

  if (phase === PHASE.COMMON) {
    currentQuestions = COMMON_QUESTIONS;
    phaseTitle = 'Temel Bilgiler';
    phaseSubtitle = 'Sizi daha iyi tanıyabilmemiz için temel sorular';
    phaseColor = '#22D3EE';
  } else if (phase === PHASE.MODULES) {
    const group = moduleGroups[currentModuleGroupIdx];
    if (group) {
      currentQuestions = group.questions;
      phaseTitle = group.title;
      phaseColor = group.color;
    }
  }

  const currentQ = currentQuestions[currentQuestionIdx];

  // ── Navigation ──
  const canGoNext = () => {
    if (!currentQ) return false;
    const val = answers[currentQ.id];
    if (currentQ.type === 'number') return val !== undefined && val !== '';
    if (currentQ.type === 'single') return val !== undefined;
    if (currentQ.type === 'yesno') return val !== undefined;
    if (currentQ.type === 'freq4') return val !== undefined;
    // Red flag questions are yesno
    if (phase === PHASE.RED_FLAGS) return val !== undefined;
    return val !== undefined;
  };

  const goNext = () => {
    if (currentQuestionIdx < currentQuestions.length - 1) {
      setCurrentQuestionIdx(prev => prev + 1);
    } else {
      if (phase === PHASE.COMMON) {
        if (moduleGroups.length > 0) {
          setPhase(PHASE.MODULES);
          setCurrentQuestionIdx(0);
          setCurrentModuleGroupIdx(0);
        } else {
          setPhase(PHASE.DOC_UPLOAD);
        }
      } else if (phase === PHASE.MODULES) {
        if (currentModuleGroupIdx < moduleGroups.length - 1) {
          setCurrentModuleGroupIdx(prev => prev + 1);
          setCurrentQuestionIdx(0);
        } else {
          setPhase(PHASE.DOC_UPLOAD);
        }
      }
    }
  };

  // goNextRef'i güncelle (auto-advance için)
  goNextRef.current = goNext;

  const goPrev = () => {
    if (currentQuestionIdx > 0) {
      setCurrentQuestionIdx(prev => prev - 1);
    } else {
      if (phase === PHASE.MODULES && currentModuleGroupIdx > 0) {
        const prevGroup = moduleGroups[currentModuleGroupIdx - 1];
        setCurrentModuleGroupIdx(prev => prev - 1);
        setCurrentQuestionIdx(prevGroup.questions.length - 1);
      } else if (phase === PHASE.MODULES) {
        setPhase(PHASE.COMMON);
        setCurrentQuestionIdx(COMMON_QUESTIONS.length - 1);
      } else if (phase === PHASE.COMMON) {
        setPhase(PHASE.AREA_SELECT);
      }
    }
  };

  // ── Save and complete ──
  const handleSubmit = async (skipDoc) => {
    setSaving(true);
    try {
      const payload = {
        responses: answers,
        selected_modules: selectedModules,
        emergency_flags: [],
        domain_question_map: DOMAIN_QUESTION_MAP,
      };
      await axios.post(`${BACKEND_URL}/api/onboarding`, payload, { withCredentials: true });
      if (!skipDoc) {
        navigate('/documents?from=onboarding');
      } else {
        await refreshUser();
        navigate('/dashboard');
      }
    } catch (err) {
      console.error('Onboarding save error:', err);
    } finally {
      setSaving(false);
    }
  };

  // ── Progress calculation ──
  const calcProgress = () => {
    if (phase === PHASE.AREA_SELECT) return 0;
    const cCount = COMMON_QUESTIONS.length;
    let modTotal = 0;
    moduleGroups.forEach(g => { modTotal += g.questions.length; });
    const total = cCount + modTotal + 1;
    let done = 0;
    if (phase === PHASE.COMMON) done = currentQuestionIdx;
    else if (phase === PHASE.MODULES) {
      done = cCount;
      for (let i = 0; i < currentModuleGroupIdx; i++) done += moduleGroups[i].questions.length;
      done += currentQuestionIdx;
    } else if (phase === PHASE.DOC_UPLOAD) done = total - 1;
    return Math.min((done / total) * 100, 100);
  };

  /* ══════════════════════════════════════════════════════════════
     RENDER: AREA SELECT
     ══════════════════════════════════════════════════════════════ */
  if (phase === PHASE.AREA_SELECT) {
    const toggleModule = (id) => {
      if (id === 'genel') {
        setSelectedModules(prev => prev.includes('genel') ? [] : ['genel']);
        return;
      }
      setSelectedModules(prev => {
        const without = prev.filter(m => m !== 'genel');
        return without.includes(id) ? without.filter(m => m !== id) : [...without, id];
      });
    };

    return (
      <div className="min-h-screen" style={{ background: 'transparent' }}>
        <PageSEO title="Sağlık Değerlendirme Başlangıcı" description="Kişisel sağlık profilinizi oluşturun" />
        <Header />
        <main className="max-w-2xl mx-auto px-4 py-8 pb-32">
          <div className="text-center mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3" data-testid="area-select-title">
              Şu anda en çok hangi konuda destek almak istiyorsunuz?
            </h1>
            <p className="text-white/50 text-sm leading-relaxed max-w-lg mx-auto">
              Seçtiğiniz konuya göre size sorulacak sorular değişecektir. Lütfen şu an öncelikli gördüğünüz alanları seçin.
            </p>
            <p className="text-white/30 text-xs mt-2">
              Birden fazla alan seçebilirsiniz. Seçtiğiniz alan sayısı arttıkça cevaplamanız gereken soru sayısı da artacaktır.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {MODULES.map(mod => {
              const Icon = ICON_MAP[mod.icon] || Shield;
              const selected = selectedModules.includes(mod.id) || (mod.id !== 'genel' && selectedModules.includes('genel'));
              const isGenel = mod.id === 'genel';
              const qCount = isGenel ? totalEstimated || '~55' : mod.questionCount;
              return (
                <button key={mod.id} onClick={() => toggleModule(mod.id)} type="button"
                  className="relative text-left p-4 rounded-2xl transition-all duration-200"
                  style={{
                    background: selected ? `${mod.color}10` : 'rgba(255,255,255,0.02)',
                    border: `1.5px solid ${selected ? `${mod.color}50` : 'rgba(255,255,255,0.06)'}`,
                    boxShadow: selected ? `0 0 20px ${mod.color}15` : 'none',
                  }}
                  data-testid={`module-card-${mod.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{
                      background: `${mod.color}15`, border: `1px solid ${mod.color}25`,
                    }}>
                      <Icon className="w-5 h-5" style={{ color: mod.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold text-sm">{mod.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full shrink-0" style={{
                          background: `${mod.color}20`, color: mod.color,
                        }}>{qCount} soru</span>
                        <p className="text-white/40 text-xs leading-relaxed truncate">{mod.description}</p>
                      </div>
                    </div>
                    <div className="w-5 h-5 rounded-md flex items-center justify-center shrink-0 mt-0.5" style={{
                      background: selected ? mod.color : 'rgba(255,255,255,0.05)',
                      border: `2px solid ${selected ? mod.color : 'rgba(255,255,255,0.12)'}`,
                    }}>
                      {selected && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {selectedModules.length > 0 && (
            <div className="mt-6 text-center">
              <p className="text-white/40 text-sm mb-4">
                Seçtiğiniz alanlara göre yaklaşık <span className="text-cyan-400 font-bold">{totalEstimated}</span> soru cevaplayacaksınız.
              </p>
              <button onClick={() => { setPhase(PHASE.COMMON); setCurrentQuestionIdx(0); }}
                className="px-8 py-3.5 rounded-2xl text-sm font-bold text-white transition-all hover:scale-105"
                style={{ background: 'linear-gradient(135deg, #22D3EE, #0891B2)', boxShadow: '0 8px 30px rgba(34,211,238,0.3)' }}
                data-testid="area-select-continue">
                Devam Et <ChevronRight className="w-4 h-4 inline ml-1" />
              </button>
            </div>
          )}
        </main>
      </div>
    );
  }

  /* ══════════════════════════════════════════════════════════════
     RENDER: DOCUMENT UPLOAD (FINAL)
     ══════════════════════════════════════════════════════════════ */
  if (phase === PHASE.DOC_UPLOAD) {
    return (
      <div className="min-h-screen flex flex-col" style={{ background: 'transparent' }}>
        <Header />
        <main className="flex-1 flex items-center justify-center px-4">
          <div className="max-w-md w-full text-center">
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{
              background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.2)',
            }}>
              <FileText className="w-10 h-10" style={{ color: '#22D3EE' }} />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3" data-testid="doc-upload-title">
              Elinizde sağlıkla ilgili belge veya rapor varsa ekleyin
            </h2>
            <p className="text-white/50 text-sm leading-relaxed mb-8">
              Kan tahlili, MR, doktor raporu, psikolojik değerlendirme veya başka sağlık belgeleri yüklerseniz, AI doktor sizi daha doğru değerlendirebilir.
            </p>
            <div className="flex flex-col gap-3">
              <button onClick={() => handleSubmit(false)} disabled={saving}
                className="w-full py-3.5 rounded-2xl text-sm font-bold text-white transition-all hover:scale-[1.02]"
                style={{ background: 'linear-gradient(135deg, #22D3EE, #0891B2)', boxShadow: '0 8px 30px rgba(34,211,238,0.3)' }}
                data-testid="doc-upload-btn">
                <Upload className="w-4 h-4 inline mr-2" /> Belge Yükle
              </button>
              <button onClick={() => handleSubmit(true)} disabled={saving}
                className="w-full py-3.5 rounded-2xl text-sm font-medium transition-all"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#94A3B8' }}
                data-testid="doc-skip-btn">
                {saving ? 'Kaydediliyor...' : 'Şimdilik Geç'}
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  /* ══════════════════════════════════════════════════════════════
     RENDER: QUESTION FLOW (Red Flags, Common, Modules)
     ══════════════════════════════════════════════════════════════ */
  const progress = calcProgress();

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'transparent' }}>
      <Header />

      {/* Progress bar */}
      <div className="h-1 w-full" style={{ background: 'rgba(255,255,255,0.03)' }}>
        <div className="h-full transition-all duration-500" style={{
          width: `${progress}%`, background: `linear-gradient(90deg, ${phaseColor}, ${phaseColor}BB)`,
          boxShadow: `0 0 10px ${phaseColor}40`,
        }} />
      </div>

      <main className="flex-1 flex flex-col max-w-lg mx-auto w-full px-4 py-6">
        {/* Phase header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            {phase === PHASE.COMMON && <User className="w-4 h-4" style={{ color: phaseColor }} />}
            {phase === PHASE.MODULES && <ClipboardCheck className="w-4 h-4" style={{ color: phaseColor }} />}
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: phaseColor }}>{phaseTitle}</span>
          </div>
          {phaseSubtitle && <p className="text-white/40 text-xs">{phaseSubtitle}</p>}
        </div>

        {/* Question card */}
        {currentQ && (
          <div className="flex-1 flex flex-col" key={`${phase}-${currentModuleGroupIdx}-${currentQuestionIdx}`}>
            <QuestionNumber num={currentQuestionIdx + 1} total={currentQuestions.length} />
            <h2 className="text-lg font-semibold text-white mb-5 leading-relaxed animate-fadeIn" data-testid="question-text">
              {currentQ.text}
            </h2>

            <div className="space-y-3 animate-fadeIn">
              {/* Common questions — auto-advance for single/yesno */}
              {phase === PHASE.COMMON && (
                <>
                  {currentQ.type === 'number' && (
                    <NumberInput id={currentQ.id} value={answers[currentQ.id]} onChange={v => setAnswer(currentQ.id, v)}
                      placeholder={currentQ.placeholder} min={currentQ.min} max={currentQ.max} />
                  )}
                  {currentQ.type === 'single' && (
                    <SingleSelect id={currentQ.id} options={currentQ.options} value={answers[currentQ.id]} onChange={v => setAnswerAndAdvance(currentQ.id, v)} />
                  )}
                  {currentQ.type === 'text' && (
                    <TextInput id={currentQ.id} value={answers[currentQ.id]} onChange={v => setAnswer(currentQ.id, v)}
                      placeholder={currentQ.placeholder} multiline={currentQ.multiline} />
                  )}
                  {currentQ.type === 'yesno' && (
                    <>
                      <YesNoInput id={currentQ.id} value={answers[currentQ.id]} onChange={v => {
                        setAnswer(currentQ.id, v);
                        if (!currentQ.followups || !currentQ.followups.some(fu => fu.showWhen === v)) {
                          setTimeout(() => goNextRef.current(), 350);
                        }
                      }} />
                      <RenderFollowups followups={currentQ.followups} answers={answers} setAnswer={setAnswer} parentAnswer={answers[currentQ.id]} />
                    </>
                  )}
                </>
              )}

              {/* Module questions — auto-advance for freq4/single/yesno */}
              {phase === PHASE.MODULES && (
                <>
                  {currentQ.type === 'freq4' && (
                    <>
                      <Freq4Input id={currentQ.id} value={answers[currentQ.id]} onChange={v => {
                        setAnswer(currentQ.id, v);
                        if (!currentQ.conditionalNote || !currentQ.conditionalNote.trigger.includes(v)) {
                          setTimeout(() => goNextRef.current(), 350);
                        }
                      }} />
                      {/* Conditional note for specific answers */}
                      {currentQ.conditionalNote && currentQ.conditionalNote.trigger.includes(answers[currentQ.id]) && (
                        <div className="mt-3 space-y-3">
                          {currentQ.conditionalNote.fields ? (
                            currentQ.conditionalNote.fields.map(f => (
                              <div key={f.id}>
                                <p className="text-white/50 text-xs mb-2">{f.text}</p>
                                {f.type === 'textarea' && <TextInput id={f.id} value={answers[f.id]} onChange={v => setAnswer(f.id, v)} multiline />}
                                {f.type === 'single' && <SingleSelect id={f.id} options={f.options} value={answers[f.id]} onChange={v => setAnswer(f.id, v)} />}
                                {f.type === 'text' && <TextInput id={f.id} value={answers[f.id]} onChange={v => setAnswer(f.id, v)} />}
                              </div>
                            ))
                          ) : (
                            <div>
                              <p className="text-white/50 text-xs mb-2">{currentQ.conditionalNote.text}</p>
                              <TextInput id={currentQ.conditionalNote.id} value={answers[currentQ.conditionalNote.id]} onChange={v => setAnswer(currentQ.conditionalNote.id, v)} />
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {currentQ.type === 'yesno' && (
                    <>
                      <YesNoInput id={currentQ.id} value={answers[currentQ.id]} onChange={v => {
                        setAnswer(currentQ.id, v);
                        if (!currentQ.followups || !currentQ.followups.some(fu => fu.showWhen === v)) {
                          setTimeout(() => goNextRef.current(), 350);
                        }
                      }} />
                      <RenderFollowups followups={currentQ.followups} answers={answers} setAnswer={setAnswer} parentAnswer={answers[currentQ.id]} />
                    </>
                  )}
                  {currentQ.type === 'single' && (
                    <SingleSelect id={currentQ.id} options={currentQ.options} value={answers[currentQ.id]} onChange={v => setAnswerAndAdvance(currentQ.id, v)} />
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Navigation — Geri + İleri/Tamamla butonları */}
        {(() => {
          const val = currentQ ? answers[currentQ.id] : undefined;
          const hasFollowup = currentQ && (
            (currentQ.type === 'yesno' && currentQ.followups && currentQ.followups.some(fu => fu.showWhen === val)) ||
            (currentQ.type === 'freq4' && currentQ.conditionalNote && currentQ.conditionalNote.trigger.includes(val))
          );
          const isTextType = currentQ && (currentQ.type === 'text' || currentQ.type === 'number');
          const needsManualNext = hasFollowup || isTextType;
          const isLastQuestion = currentQuestionIdx === currentQuestions.length - 1;
          const isLastGroup = phase === PHASE.MODULES && currentModuleGroupIdx === moduleGroups.length - 1;
          const isVeryLast = isLastQuestion && (phase === PHASE.COMMON ? moduleGroups.length === 0 : isLastGroup);
          const canAdvance = val !== undefined && val !== '';

          return (
            <div className="flex items-center justify-between mt-8 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              <button onClick={goPrev}
                className="flex items-center gap-1 px-4 py-2.5 rounded-xl text-sm font-medium transition-all"
                style={{ color: '#94A3B8', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
                data-testid="onboarding-prev">
                <ChevronLeft className="w-4 h-4" /> Geri
              </button>
              {needsManualNext ? (
                <button onClick={goNext} disabled={!canAdvance}
                  className="flex items-center gap-1.5 px-6 py-2.5 rounded-xl text-sm font-bold transition-all disabled:opacity-30"
                  style={{ background: isVeryLast ? 'linear-gradient(135deg, #FACC15, #F59E0B)' : `${phaseColor}20`, color: isVeryLast ? '#1A2B3C' : phaseColor, border: isVeryLast ? 'none' : `1px solid ${phaseColor}35` }}
                  data-testid="onboarding-next">
                  {isVeryLast ? 'Tamamla' : 'İleri'} <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <span className="text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>
                  Cevabı seçtiğinizde otomatik ilerler
                </span>
              )}
            </div>
          );
        })()}
      </main>

    </div>
  );
}
